import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { registerFormattingRailToolbarPress } from "./formattingRailToolbarPress";
import { renderEyebrowBlock, eyebrowTextStyle } from "../render/blocks/eyebrow";
import { selectionEquals } from "../domain/selection/equals";
import { isTextBlockEditing } from "../domain/text-editing";
import {
  selectEmailSettings,
  selectSelection,
  selectTextEditingBlock,
  selectTextEditReentryGuard,
} from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/store";
import type { EyebrowBlock } from "../domain/types";
import { InlineEyebrowLabel } from "./InlineEyebrowLabel";
import { useEditorFacility } from "./EditorFacilityContext";
import { useEditableButtonEditSession } from "./useEditableButtonEditSession";
import { useEditableButtonPointer } from "./useEditableButtonPointer";
import { isEmailBuilderRichTextEmpty } from "../editing/richText/emailBuilderRichTextHtml";

type EditableEyebrowProps = {
  rowId: string;
  columnId: string;
  block: EyebrowBlock;
};

export function EditableEyebrow({ rowId, columnId, block }: EditableEyebrowProps) {
  const { t } = useTranslation();
  const facility = useEditorFacility();
  const selection = useEditorStore(selectSelection);
  const textTheme = useEditorStore(selectEmailSettings);
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const textEditReentryGuard = useEditorStore(selectTextEditReentryGuard);
  const {
    updateBlock,
    setSelection,
    setTextEditingBlock,
    setTextEditReentryGuard,
  } = useEditorActions();
  const rootRef = useRef<HTMLDivElement>(null);
  const exitingRef = useRef(false);
  const toolbarPressRef = useRef(false);
  useEffect(
    () => registerFormattingRailToolbarPress(block.id, toolbarPressRef),
    [block.id],
  );
  const target = { rowId, columnId, blockId: block.id };
  const selected = selectionEquals(selection, { kind: "block", ...target });
  const isEditing = isTextBlockEditing(textEditingBlock, rowId, columnId, block.id);
  const isEmpty = isEmailBuilderRichTextEmpty(block.text);

  const { enterEdit } = useEditableButtonEditSession({
    rowId,
    columnId,
    block,
    isEditing,
    target,
    setSelection,
    setTextEditingBlock,
    updateBlock,
    setTextEditReentryGuard,
    rootRef,
    exitingRef,
    toolbarPressRef,
  });

  const { rootHandlers } = useEditableButtonPointer({
    block,
    isEditing,
    selected,
    textEditReentryGuard,
    setSelection,
    setTextEditReentryGuard,
    target,
    enterEdit,
  });

  return (
    <div
      ref={rootRef}
      data-button-editing={isEditing ? "true" : undefined}
      {...rootHandlers}>
      {renderEyebrowBlock({
        block,
        facility,
        textTheme,
        mergeMode: "raw",
        textSlot: isEditing ? (
          <InlineEyebrowLabel block={block} />
        ) : isEmpty ? (
          <span
            className="email-builder-canvas-block-placeholder"
            style={eyebrowTextStyle(block, textTheme)}
            aria-hidden="true">
            {t("Eyebrow")}
          </span>
        ) : undefined,
      })}
    </div>
  );
}
